import { create } from 'zustand'
import { HookResult } from '../hooks/hookTypes'
import axiosInstance, { axiosRequest } from '../network/axiosInstance'

// Define the Organization interface based on your schema
export interface Organization {
  id: string
  name: string
  license?: string
  owner: string
  OpenAIApiKey?: string
  MistralAPIKey?: string
  ClaudeAPIKey?: string
  settings?: string
  generalInformation?: string
  addons?: string[]
  billingEmail?: string
  billingName?: string
  billingAddress?: AddressType
  billingPlan?: string
  subscriptionStatus?: string
  subscriptionId?: string
  paymentMethod?: any
  taxId?: string
  invoiceSettings?: any
  billingHistory?: any
  contactEmail?: string
  contactPhone?: string
  website?: string
  address?: AddressType
  projectIds?: string[]
  maxUsers?: number
  maxProjects?: number
  maxStorage?: number
  features?: string[]
  createdAt: string
  updatedAt: string
}

// Define the address structure
export interface AddressType {
  street?: string
  city?: string
  state?: string
  postalCode?: string
  country?: string
}

export interface User {
  id: string
  name: string
  email: string
  organizationId?: string
  organizationIds?: string[]
}

export interface OrganizationsState {
  organizationsLoading: boolean
  organizationsFetchSuccess: boolean
  organizationsFetchError: boolean
  organizations: Organization[] | null
  organizationsErrorMessage: string | null
  fetchOrganizations: () => Promise<void>
}

// Create a Zustand store for fetching organizations
export const useGetOrganizations = create<OrganizationsState>((set) => ({
  organizationsLoading: false,
  organizationsFetchSuccess: false,
  organizationsFetchError: false,
  organizations: null,
  organizationsErrorMessage: null,
  fetchOrganizations: async () => {
    console.log('fetchOrganizations called')
    set({
      organizationsLoading: true,
      organizationsFetchSuccess: false,
      organizationsFetchError: false,
      organizations: null,
      organizationsErrorMessage: null,
    })
    try {
      const res = await axiosInstance.get('/api/organizations')
      set({
        organizationsLoading: false,
        organizationsFetchSuccess: true,
        organizations: res.data,
      })
    } catch (err) {
      console.error('Error fetching organizations:', err)
      set({
        organizationsLoading: false,
        organizationsFetchError: true,
        organizationsErrorMessage: (err as Error).message,
      })
    }
  },
}))

// Create new organization
export const useCreateOrganization = axiosRequest({
  method: 'POST',
  url: '/api/organizations',
})

// Get organization by ID
export const useGetOrganization = axiosRequest({
  method: 'GET',
  url: '/api/organizations/:id',
})

// Get organization with users
export const useGetOrganizationWithUsers = axiosRequest({
  method: 'GET',
  url: '/api/organizations/:id/users',
})

// Update organization
export const useUpdateOrganization = axiosRequest({
  method: 'PUT',
  url: '/api/organizations/:id',
})

// Update organization billing information
export const useUpdateOrganizationBilling = axiosRequest({
  method: 'PUT',
  url: '/api/organizations/:id/billing',
})

// Update organization contact information
export const useUpdateOrganizationContact = axiosRequest({
  method: 'PUT',
  url: '/api/organizations/:id/contact',
})

// Update organization settings
export const useUpdateOrganizationSettings = axiosRequest({
  method: 'PUT',
  url: '/api/organizations/:id/settings',
})

// Delete organization
export const useDeleteOrganization = axiosRequest({
  method: 'DELETE',
  url: '/api/organizations/:id',
})

// Get organization members
export const useGetOrganizationMembers = axiosRequest({
  method: 'GET',
  url: '/api/organizations/:id/members',
})

// Add member to organization
export const useAddMemberToOrganization = axiosRequest({
  method: 'POST',
  url: '/api/organizations/:id/members',
})

// Remove member from organization
export const useRemoveMemberFromOrganization = axiosRequest({
  method: 'DELETE',
  url: '/api/organizations/:id/members/:userId',
})

// Set primary organization
export const useSetPrimaryOrganization = axiosRequest({
  method: 'PUT',
  url: '/api/organizations/:id/primary',
})

// Assign project to organization
export const useAssignProjectToOrganization = axiosRequest({
  method: 'POST',
  url: '/api/organizations/:id/projects',
})

// Remove project from organization
export const useRemoveProjectFromOrganization = axiosRequest({
  method: 'DELETE',
  url: '/api/organizations/:id/projects/:projectId',
})

// Get organization projects
export const useGetOrganizationProjects = axiosRequest({
  method: 'GET',
  url: '/api/organizations/:id/projects',
})

// Transfer organization ownership
export const useTransferOrganizationOwnership = axiosRequest({
  method: 'PUT',
  url: '/api/organizations/:id/ownership',
})

export type { Organization }